import { useEffect, useState } from 'react';
import { ExternalLink, LogOut, Plus } from 'lucide-react';
import { supabase } from '../../shared/api/supabase-client';
import { partnerApi, type PartnerAccount, type PartnerPost } from '../../shared/api/partner.api';
import { num, PARTNER_PERIODS, periodRange, postDay, type PartnerPeriod } from './period';
import { ConnectAccountDialog, type ConnectPlatform } from '../../features/account-add';
import { platformLabel } from '../../entities/account';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { toast } from 'sonner@2.0.3';

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

/**
 * Всё, что есть у партнёра (23.10.2026): подключить свои Instagram-аккаунты
 * (с 27.10 и TikTok, с 28.10 YouTube ссылкой и Threads) и увидеть, что подключено. Наших пабликов, публикаций и разделов здесь нет —
 * и сервер их партнёру не отдаёт.
 */
export function PartnerPage() {
  const [data, setData] = useState<{ name: string | null; accounts: PartnerAccount[] } | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);

  const load = () => partnerApi.me().then((d) => { setData(d); return d; }).catch((e) => { toast.error(e.message); return null; });
  useEffect(() => { void load(); }, []);

  // Окно входа TikTok/Threads могло закрыться без ответа (возврат на другой
  // домен сайта) — тогда о подключении узнаём по свежему списку.
  const handleConnected = async (platform: ConnectPlatform, outcome: 'connected' | 'closed') => {
    const before = new Set(data?.accounts.map((a) => a.id));
    const fresh = (await load())?.accounts.find((a) => a.platform === platform && !before.has(a.id));
    if (outcome === 'closed' && fresh) {
      toast.success(`${platformLabel(platform)} @${fresh.username} подключён`);
      setConnectOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto flex max-w-xl flex-col gap-6">
        <header className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold">InstaFarm</h1>
            <p className="truncate text-sm text-muted-foreground">{data?.name ?? 'Партнёр'}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => void supabase.auth.signOut()}>
            <LogOut />
            Выйти
          </Button>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Ваши аккаунты</CardTitle>
            <CardDescription>
              Подключите аккаунты, чтобы мы видели статистику ваших видео. Публиковать от вашего имени и читать сообщения мы не можем — доступ только к профилю и статистике.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {!data ? (
              <p className="text-sm text-muted-foreground">Загружаем…</p>
            ) : data.accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Пока ни одного аккаунта.</p>
            ) : (
              <ul className="flex flex-col divide-y rounded-md border">
                {data.accounts.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 p-3">
                    <Avatar className="size-9">
                      <AvatarImage src={a.profilePicture ?? undefined} alt="" />
                      <AvatarFallback className="text-xs">{a.username.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        @{a.username}
                        <span className="ml-1.5 text-xs font-normal text-muted-foreground">{platformLabel(a.platform)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {a.followersCount != null ? `${a.followersCount.toLocaleString('ru-RU')} подписчиков · ` : ''}подключён {day(a.connectedAt)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <Button onClick={() => setConnectOpen(true)}>
                <Plus />
                Подключить аккаунт
              </Button>
              <span className="text-xs text-muted-foreground">Instagram, TikTok, YouTube или Threads</span>
            </div>
            <p className="text-xs text-muted-foreground">Подключили по ошибке или хотите отключить — напишите нам.</p>
            <ConnectAccountDialog open={connectOpen} onOpenChange={setConnectOpen} audience="partner" onConnected={handleConnected} />
          </CardContent>
        </Card>

        {Boolean(data?.accounts.length) && <PartnerPosts />}
      </div>
    </div>
  );
}

/**
 * Свои публикации и их просмотры (25.10.2026): собираются сами из
 * подключённых аккаунтов раз в сутки — партнёру ничего присылать не нужно.
 */
function PartnerPosts() {
  const [period, setPeriod] = useState<PartnerPeriod>('30');
  const [data, setData] = useState<{ totals: { posts: number; views: number | null }; posts: PartnerPost[] } | null>(null);
  useEffect(() => {
    setData(null);
    const { from, to } = periodRange(period);
    partnerApi.myPosts(from, to).then(setData).catch((e) => toast.error(e.message));
  }, [period]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ваши публикации</CardTitle>
        <CardDescription>Ссылки и просмотры обновляются сами раз в сутки.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {PARTNER_PERIODS.map((p) => (
            <Button key={p.value} size="sm" variant={period === p.value ? 'default' : 'outline'} onClick={() => setPeriod(p.value)}>{p.label}</Button>
          ))}
        </div>
        {!data ? (
          <p className="text-sm text-muted-foreground">Загружаем…</p>
        ) : (
          <>
            <p className="text-sm">
              <b>{data.totals.posts}</b> публикаций · <b>{num(data.totals.views)}</b> просмотров
            </p>
            {data.posts.length === 0 ? (
              <p className="text-sm text-muted-foreground">За этот период публикаций нет. Новые появятся на следующий день после выхода.</p>
            ) : (
              <ul className="flex flex-col divide-y rounded-md border">
                {data.posts.map((p) => (
                  <li key={p.mediaId} className="flex items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">@{p.account ?? '—'} · {postDay(p.publishedAt)}</div>
                      <div className="text-xs text-muted-foreground">{num(p.views)} просмотров · {num(p.likes)} лайков</div>
                    </div>
                    {p.link && (
                      <a href={p.link} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Открыть публикацию">
                        <ExternalLink className="size-4" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
